import { GshopListingStatus } from "@redotech/cockroachdb-codegen/tables/product/gshop-listing-status";
import { GshopProductFieldType } from "@redotech/cockroachdb-codegen/tables/product/gshop-product-field-type";
import { googleShoppingListingIdFormat } from "@redotech/redo-marketplace-id";
import { attributeDefinitionIdFormat } from "@redotech/redo-product-id";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { AttributeType, AttributeValueSchema } from "../attributes";
import { BaseProductFamilyListingSchema, ListingType } from "../base-listing";
import { RedoListingStatus } from "../redo-listing-status";
import {
  GoogleShoppingListingMediaSchema,
  GoogleShoppingListingMediaWithGoogleIds,
  GoogleShoppingListingMediaWithoutRedoIds,
} from "./google-shopping-media";
import {
  GoogleShoppingVariantListingSchema,
  GoogleShoppingVariantListingWithGoogleIds,
  GoogleShoppingVariantListingWithoutRedoIds,
  GoogleShoppingVariantMappingSchema,
} from "./google-shopping-variant";

export const GoogleShoppingListingStatusSchema = createEnum(GshopListingStatus);

export const GoogleShoppingListingIdWithStatusSchema = z.object({
  id: googleShoppingListingIdFormat.schema,
  status: GoogleShoppingListingStatusSchema,
});
export type GoogleShoppingListingIdWithStatus = z.infer<
  typeof GoogleShoppingListingIdWithStatusSchema
>;

export function convertGoogleShoppingListingStatusToRedoListingStatus(
  status: GshopListingStatus,
): RedoListingStatus {
  switch (status) {
    case GshopListingStatus.Active:
      return RedoListingStatus.ACTIVE;
    case GshopListingStatus.Draft:
      return RedoListingStatus.DRAFT;
    case GshopListingStatus.Disapproved:
      return RedoListingStatus.INACTIVE;
    case GshopListingStatus.Pending:
      return RedoListingStatus.DRAFT;
    default:
      assertNever(status);
  }
}

export const GshopProductFieldTypeSchema = createEnum(GshopProductFieldType);

export const GoogleShoppingListingSchema_ =
  BaseProductFamilyListingSchema.extend({
    listingType: z.literal(ListingType.GoogleShopping),
    id: z.bigint(),
    googleProductId: z.string().nullable(),
    title: z.string(),
    description: z.string(),
    brand: z.string().nullable(),
    googleProductCategory: z.string().nullable(),
    productType: z.string().nullable(),
    link: z.string(),
    customLabel0: z.string().nullable(),
    customLabel1: z.string().nullable(),
    customLabel2: z.string().nullable(),
    customLabel3: z.string().nullable(),
    customLabel4: z.string().nullable(),
    feedLabel: z.string(),
    contentLanguage: z.string(),
    status: GoogleShoppingListingStatusSchema,
    variants: z.array(GoogleShoppingVariantListingSchema),
    media: z.array(GoogleShoppingListingMediaSchema),
    createdAt: z.date(),
    updatedAt: z.date(),
  });
export const GoogleShoppingListingSchema: z.ZodType<GoogleShoppingListing> =
  GoogleShoppingListingSchema_;
export interface GoogleShoppingListing extends z.infer<
  typeof GoogleShoppingListingSchema_
> {}

export type GoogleShoppingListingWithoutRedoIds = Omit<
  GoogleShoppingListing,
  "id" | "variants" | "media" | "productFamilyId"
> & {
  variants: GoogleShoppingVariantListingWithoutRedoIds[];
  media: GoogleShoppingListingMediaWithoutRedoIds[];
};

export type GoogleShoppingListingWithGoogleIds = Omit<
  GoogleShoppingListing,
  "id" | "variants" | "media"
> & {
  googleProductId: string;
  variants: GoogleShoppingVariantListingWithGoogleIds[];
  media: GoogleShoppingListingMediaWithGoogleIds[];
};

export const GoogleShoppingProductAttributeType = {
  [GshopProductFieldType.Title]: AttributeType.ShortText,
  [GshopProductFieldType.Description]: AttributeType.RichText,
  [GshopProductFieldType.Brand]: AttributeType.ShortText,
  [GshopProductFieldType.GoogleProductCategory]: AttributeType.ShortText,
  [GshopProductFieldType.ProductType]: AttributeType.ShortText,
  [GshopProductFieldType.Link]: AttributeType.ShortText,
  [GshopProductFieldType.CustomLabel0]: AttributeType.ShortText,
  [GshopProductFieldType.CustomLabel1]: AttributeType.ShortText,
  [GshopProductFieldType.CustomLabel2]: AttributeType.ShortText,
  [GshopProductFieldType.CustomLabel3]: AttributeType.ShortText,
  [GshopProductFieldType.CustomLabel4]: AttributeType.ShortText,
} satisfies Record<GshopProductFieldType, AttributeType>;

const GoogleShoppingProductMappingSchema_ = z.object({
  fieldName: GshopProductFieldTypeSchema,
  attributeDefinitionId: attributeDefinitionIdFormat.schema,
});
export const GoogleShoppingProductMappingSchema: z.ZodType<GoogleShoppingProductMapping> =
  GoogleShoppingProductMappingSchema_;
export interface GoogleShoppingProductMapping extends z.infer<
  typeof GoogleShoppingProductMappingSchema_
> {}

export const GoogleShoppingMappingSchema_ = z.object({
  productMappings: z.array(GoogleShoppingProductMappingSchema).readonly(),
  variantMappings: z.array(GoogleShoppingVariantMappingSchema).readonly(),
});
export const GoogleShoppingMappingSchema: z.ZodType<GoogleShoppingMapping> =
  GoogleShoppingMappingSchema_;
export interface GoogleShoppingMapping extends z.infer<
  typeof GoogleShoppingMappingSchema_
> {}

export const GoogleShoppingProductAttributeSchema = {
  [GshopProductFieldType.Title]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[GshopProductFieldType.Title]
    ].nullable(),
  [GshopProductFieldType.Description]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[GshopProductFieldType.Description]
    ].nullable(),
  [GshopProductFieldType.Brand]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[GshopProductFieldType.Brand]
    ].nullable(),
  [GshopProductFieldType.GoogleProductCategory]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[
        GshopProductFieldType.GoogleProductCategory
      ]
    ].nullable(),
  [GshopProductFieldType.ProductType]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[GshopProductFieldType.ProductType]
    ].nullable(),
  [GshopProductFieldType.Link]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[GshopProductFieldType.Link]
    ].nullable(),
  [GshopProductFieldType.CustomLabel0]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[GshopProductFieldType.CustomLabel0]
    ].nullable(),
  [GshopProductFieldType.CustomLabel1]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[GshopProductFieldType.CustomLabel1]
    ].nullable(),
  [GshopProductFieldType.CustomLabel2]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[GshopProductFieldType.CustomLabel2]
    ].nullable(),
  [GshopProductFieldType.CustomLabel3]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[GshopProductFieldType.CustomLabel3]
    ].nullable(),
  [GshopProductFieldType.CustomLabel4]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[GshopProductFieldType.CustomLabel4]
    ].nullable(),
};

export type GoogleShoppingProductAttributeValueType = {
  [key in GshopProductFieldType]: (typeof GoogleShoppingProductAttributeSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export function getGoogleShoppingProductAttributeValue<
  T extends GshopProductFieldType,
>(field: T, value: unknown): GoogleShoppingProductAttributeValueType[T] | null {
  return GoogleShoppingProductAttributeSchema[field].parse(
    value,
  ) as GoogleShoppingProductAttributeValueType[T];
}

export function getGoogleShoppingProductAttributeType<
  T extends GshopProductFieldType,
>(field: T): (typeof GoogleShoppingProductAttributeType)[T] {
  return GoogleShoppingProductAttributeType[field];
}
